/**
 * 0.45.10 - FAQ fed by a library or a collection.
 *
 * Each row of the library is one question and its answer. The admin
 * picks the two columns on the FAQ element (question_column,
 * answer_column). Rows come from the public rows endpoint in the
 * visitor's language, so translatable columns show translated text.
 * Rows with an empty question are skipped. The answer is Markdown,
 * as in a hand-written FAQ (a collection's rich-text column is read
 * from its Markdown twin, <column>_md).
 *
 * Self-fetching, so the page, the preview and the editor canvas all
 * show the real rows the same way.
 */
import { useEffect, useState } from 'react';
import FaqAccordion, { filterFaqItems } from './FaqAccordion';
import { formLibraryRowsApi } from '../api/admin';
import { useLanguage } from '../context/LanguageContext';

function cellText(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

// Rows -> [{question, answer}], skipping rows without a question.
export function faqItemsFromRows(rows, questionColumn, answerColumn) {
  if (!Array.isArray(rows) || !questionColumn) return [];
  const out = [];
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    const question = cellText(r[questionColumn]).trim();
    if (!question) continue;
    // A collection's rich-text column also comes as <column>_md, its
    // Markdown source. Prefer that, so headings, lists and links keep
    // their formatting in the answer.
    const md = answerColumn ? r[`${answerColumn}_md`] : undefined;
    const answer = typeof md === 'string' ? md
      : (answerColumn ? cellText(r[answerColumn]) : '');
    out.push({ question, answer });
  }
  return out;
}

export default function LibraryFaq({ library, questionColumn, answerColumn,
                                     emptyText = null, fill = false,
                                     search = null, searchEmptyShowsNone = false,
                                     highlightColor = null, colorMatches = true }) {
  const { language } = useLanguage();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!library) { setRows([]); return undefined; }
    let cancelled = false;
    formLibraryRowsApi.list(library, { lang: language })
      .then((data) => { if (!cancelled) setRows(Array.isArray(data && data.rows) ? data.rows : []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [library, language]);

  if (rows === null) return null;
  const all = faqItemsFromRows(rows, questionColumn, answerColumn);
  // 0.45.29 - A linked Search element narrows the list (null: no search).
  const items = search == null ? all : filterFaqItems(all, search, searchEmptyShowsNone);
  if (all.length === 0) {
    return emptyText
      ? <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-600)',
                    fontStyle: 'italic', margin: 0 }}>{emptyText}</p>
      : null;
  }
  // 0.45.32 - Kept mounted during a search that matches nothing.
  if (items.length === 0 && search == null) return null;
  return <FaqAccordion items={items} fill={fill} highlight={search || ''} highlightColor={highlightColor} colorMatches={colorMatches} />;
}
